import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUser } from '@fortawesome/free-solid-svg-icons';

// Actions
import { gameActions, modalActions } from '../actions';

const PlayersManager = ({ roleLabels = [], disabled = false }) => {
  // State
  const [item, setItem] = useState({});

  // Redux
  const game = useSelector((state) => state.game);

  // Hooks
  const dispatch = useDispatch();

  // Effect: Component initialisation
  useEffect(() => {
    if (game?.data) {
      setItem(game.data);
    }
  }, [game?.data]);

  // Methods
  const handleGroupChange = (email, e) => {
    // console.log(email, e.target.value);

    const gindex = parseInt(e.target.value);

    // Update the player's group
    dispatch(
      gameActions.updateRegisteredPlayer({
        _id: item._id,
        email,
        gindex,
      })
    );
  };

  const removePlayerModal = (e, user) => {
    const username = `${user.name} ${user.lastname} ${user.email}`.trim();

    dispatch(
      modalActions.open({
        title: 'Remove Player',
        body: (
          <div>
            <p>
              Are you sure you want to remove <b>{username}</b> from this game?
            </p>
          </div>
        ),
        buttons: [
          <button
            key='btn-2'
            className='btn btn-outline-secondary'
            onClick={() => dispatch(modalActions.close())}>
            Cancel
          </button>,
          <button
            key='btn-1'
            className='btn btn-danger'
            onClick={() => {
              dispatch(gameActions.removeRegistered(item._id, [user?.email]));
              dispatch(modalActions.close());
            }}>
            Remove
          </button>,
        ],
      })
    );
  };

  // Logic
  const ppg = item?.ppg || 1;

  // Let's use item.pgg to calculate the number of potential groups
  const fullGroups = item.registered?.length % item?.ppg === 0 ? true : false;
  const numGroups = item.registered
    ? Math.ceil(item.registered?.length / item?.ppg) + (fullGroups ? 1 : 0)
    : 0;

  // Let's also collect the groupes that are currently specified in the registered players gindex
  const currentGroups =
    item.registered
      ?.filter((rp) => typeof rp.gindex !== 'undefined')
      ?.map((rp) => rp.gindex) ?? [];
  const totalGroupsArray = [...Array(numGroups).keys()]?.map((g) => g + 1);
  const totalGroups = [...totalGroupsArray, ...currentGroups].sort();
  const uniqueGroupsIds = [...new Set(totalGroups)];

  // Sort registered players by group
  const registeredPlayers = item.registered
    ? [...item.registered].sort((a, b) => {
        if (a.gindex < b.gindex) return -1;
        if (a.gindex > b.gindex) return 1;
        return 0;
      })
    : [];

  // console.log({ game, item, registeredPlayers });

  return (
    <ul className='list-group'>
      {registeredPlayers.map((rp) => {
        const bgClass =
          rp.gindex % 2 === 0 ? 'bg-white' : 'bg-light bg-opacity-10';

        const thisGroupPlayers = registeredPlayers.filter(
          (rp2) => rp2.gindex === rp.gindex
        );
        const isGroupFull = thisGroupPlayers.length >= ppg;

        return (
          <li
            key={`li-${rp.email}`}
            className={`list-group-item d-flex align-items-center justify-content-between ${bgClass}`}>
            <small className='w-50'>
              {rp.email}{' '}
              <button
                title='Remove player'
                className='btn btn-sm btn-link text-danger small ms-1 lh-1'
                disabled={disabled}
                onClick={(e) => removePlayerModal(e, rp)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <br />
              {/* Full name */}
              <span className='text-secondary'>
                {rp?.name} {rp?.lastname}
              </span>
              {/* Display role label or rindex */}
              {roleLabels?.[rp?.rindex]?.length > 0 ? (
                <span className='text-secondary ms-4'>
                  Role: {roleLabels?.[rp?.rindex]}
                </span>
              ) : null}
            </small>

            <small
              className={`small ms-auto me-3 fst-italic opacity-50 ${
                isGroupFull ? 'text-success' : 'text-dark'
              }`}>
              <FontAwesomeIcon icon={faUser} className='me-1' />x{' '}
              {thisGroupPlayers.length}
            </small>

            <span className='word-break'>
              <select
                key={`sel-${rp.email}-${rp.gindex}`}
                className='form-select form-select-sm'
                disabled={disabled}
                defaultValue={rp?.gindex}
                onChange={(e) => handleGroupChange(rp.email, e)}>
                {uniqueGroupsIds.map((g) => (
                  <option key={`opt-${g}`} value={g}>
                    Group {g}
                  </option>
                ))}
              </select>
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export default PlayersManager;
